import { v4 as uuidv4 } from 'uuid';
import * as repr from './types';

const baseUrl = '/proposal-builder';

export default class Resource<T> {
    data: T;

    constructor(data: T) {
        this.data = data;
    }
}

export function getCookieFromLocalStorage(cookieName: string) {
    // Retrieve all cookies from local storage
    const cookiesString = localStorage.getItem('cookies');
    
    // Parse the cookies string into an object
    const cookies = cookiesString ? JSON.parse(cookiesString) : {};
    
    // Return the value of the specified cookie
    return cookies[cookieName];
}

export const getProposals = async (
    authToken: string | null,
    type?: repr.investmentTypes
): Promise<Resource<repr.ProposalsPayload>> => {
    const url = `${baseUrl}/api/Proposals`;
    const urlParams = new URLSearchParams({ cacheBustId: uuidv4() });
    
    if (type) {
        urlParams.append('type', type);
    }
    
    const resp = await fetch(`${url}?${urlParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            ...authToken ? { Authorization: `Bearer ${authToken}` } : {}
        }
    });

    if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(errorText);
    }

    const json = await resp.json();
    return new Resource(json as repr.ProposalsPayload);
};

export const getProposal = async (
    authToken: string | null,
    proposalId: string,
    locale: string
): Promise<Resource<repr.ProposalDetailed>> => {
    const url = `${baseUrl}/api/Proposals/${encodeURIComponent(proposalId)}`;
    const urlParams = new URLSearchParams({ 
        cacheBustId: uuidv4(),
        locale: locale
    });
    
    const resp = await fetch(`${url}?${urlParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            ...authToken ? { Authorization: `Bearer ${authToken}` } : {}
        }
    });

    if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(errorText);
    }

    const json = await resp.json();
    return new Resource(json as repr.ProposalDetailed);
};;

export const postProposal = async (
    authToken: string | null,
    userId: string | null,
    data: Partial<repr.ProposalDetailed>
): Promise<Resource<{}>> => {
    // If id is part of the data object then this will update the
    // existing proposal else it will create a new one
    const body = { ...data, createdBy: userId };
    const url = `${baseUrl}/api/Proposals`;
    const urlParams = new URLSearchParams({ cacheBustId: uuidv4() });
    
    const xsrfToken = getCookieFromLocalStorage('XSRF-TOKEN');
    
    const resp = await fetch(`${url}?${urlParams.toString()}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': xsrfToken,
            ...authToken ? { Authorization: `Bearer ${authToken}` } : {}
        },
        body: JSON.stringify(body)
    });

    if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(errorText);
    }

    return new Resource({} as {});
};

export const deleteProposal = async (
    authToken: string | null,
    proposalId: string
): Promise<Resource<{}>> => {
    const url = `${baseUrl}/api/Proposals/${encodeURIComponent(proposalId)}`;
    const urlParams = new URLSearchParams({ cacheBustId: uuidv4() });
    
    const xsrfToken = getCookieFromLocalStorage('XSRF-TOKEN');
    
    const resp = await fetch(`${url}?${urlParams.toString()}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
            'X-XSRF-TOKEN': xsrfToken,
            ...authToken ? { Authorization: `Bearer ${authToken}` } : {}
        }
    });

    if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(errorText);
    }

    return new Resource({} as {});
};

export const getFunds = async (
    authToken: string | null,
    streamType: repr.investmentTypes,
    isFeeBased: boolean,
    locale: string,
    productType: repr.productType,
    ageRestriction: number
): Promise<Resource<repr.FundsPayload>> => {
    const url = `${baseUrl}/api/Funds`;
    const urlParams = new URLSearchParams({ cacheBustId: uuidv4() });
    
    const bodyData: Record<string, any> = {
        // TODO: api expects lowercase streamType but everywhere else
        // uses 'MMF' | 'Seg' (api change needed)
        streamType: streamType.toLowerCase(),
        isFeeBased: isFeeBased,
        locale: locale
    };

    if (productType && streamType.toLowerCase() === "seg") {
        if (productType.toLowerCase().includes("mpip")) {
            bodyData.productType = "mpip75,mpip100";
        } else {
            bodyData.productType = "gif75,gif100";
        }
    }

    if (ageRestriction > 0) {
        bodyData.clientAge = ageRestriction;
    }

    const xsrfToken = getCookieFromLocalStorage('XSRF-TOKEN');
    
    const resp = await fetch(`${url}?${urlParams.toString()}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': xsrfToken,
            ...authToken ? { Authorization: `Bearer ${authToken}` } : {}
        },
        body: JSON.stringify(bodyData)
    });

    if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(errorText);
    }

    const json = await resp.json();
    return new Resource(json as repr.FundsPayload);
};

export const getGiaFunds = async (
    authToken: string | null,
    locale: string
): Promise<Resource<repr.FundsPayload>> => {
    const url = `${baseUrl}/api/Funds/Gia`;
    const urlParams = new URLSearchParams({ 
        cacheBustId: uuidv4(),
        locale: locale
    });
    
    const resp = await fetch(`${url}?${urlParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            ...authToken ? { Authorization: `Bearer ${authToken}` } : {}
        }
    });

    if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(errorText);
    }

    const json = await resp.json();
    return new Resource(json as repr.FundsPayload);
};

export const getEtfFunds = async (
    authToken: string | null,
    locale: string
): Promise<Resource<repr.FundsPayload>> => {
    const url = `${baseUrl}/api/Funds/etf`;
    const urlParams = new URLSearchParams({ 
        cacheBustId: uuidv4(),
        locale: locale
    });
    
    const resp = await fetch(`${url}?${urlParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            ...authToken ? { Authorization: `Bearer ${authToken}` } : {}
        }
    });

    if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(errorText);
    }

    const json = await resp.json();
    return new Resource(json as repr.FundsPayload);
};

export const postLocalization = async (
    authToken: string | null,
    locale: string,
    returnUrl?: string
): Promise<Resource<{}>> => {
    const url = `${baseUrl}/Localization`;
    const urlParams = new URLSearchParams({ 
        cacheBustId: uuidv4(),
        culture: locale
    });

    if (returnUrl) {
        urlParams.append('returnUrl', returnUrl);
    }

    const xsrfToken = getCookieFromLocalStorage('XSRF-TOKEN');
    
    const resp = await fetch(`${url}?${urlParams.toString()}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': xsrfToken,
            ...authToken ? { Authorization: `Bearer ${authToken}` } : {}
        },
        body: JSON.stringify({})
    });

    if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(errorText);
    }

    return new Resource({} as {});
};

export const getReport = async (
    authToken: string | null,
    proposalId: string,
    clientId: string | null,
    locale: 'en-US' | 'fr-CA'
): Promise<Resource<repr.Report>> => {
    const url = `${baseUrl}/api/Reports/${encodeURIComponent(proposalId)}/${encodeURIComponent(locale)}/view`;
    const urlParams = new URLSearchParams({ cacheBustId: uuidv4() });

    if (clientId) {
        urlParams.append('clientId', clientId);
    }
    
    const resp = await fetch(`${url}?${urlParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            ...authToken ? { Authorization: `Bearer ${authToken}` } : {}
        }
    });

    if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(errorText);
    }

    const json = await resp.json();
    return new Resource(json as repr.Report);
};

export const getPDFReportId = async (
    authToken: string | null,
    proposalId: string,
    clientId: string | null
): Promise<Resource<repr.PDFReportPayload>> => {
    const url = `${baseUrl}/api/Reports/${encodeURIComponent(proposalId)}/pdfs`;
    const urlParams = new URLSearchParams({ cacheBustId: uuidv4() });

    if (clientId) {
        urlParams.append('clientId', clientId);
    }
    
    const resp = await fetch(`${url}?${urlParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            ...authToken ? { Authorization: `Bearer ${authToken}` } : {}
        }
    });

    if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(errorText);
    }

    const json = await resp.json();
    return new Resource(json as repr.PDFReportPayload);
};

export const getPDFReportUrl = async (
    authToken: string | null,
    reportlId: string,
    locale: 'en-US' | 'fr-CA'
): Promise<Resource<repr.ReportUrlPayload>> => {
    const url = `${baseUrl}/api/Reports/${encodeURIComponent(reportlId)}/${encodeURIComponent(locale)}/download`;
    const urlParams = new URLSearchParams({ cacheBustId: uuidv4() });
    
    const resp = await fetch(`${url}?${urlParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            ...authToken ? { Authorization: `Bearer ${authToken}` } : {}
        }
    });

    if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(errorText);
    }

    const json = await resp.json();
    return new Resource(json as repr.ReportUrlPayload);
};

export const getCurrentUser = async (
    authToken: string | null
): Promise<Resource<repr.User>> => {
    const url = `${baseUrl}/api/Users/currentUser`;
    const urlParams = new URLSearchParams({ cacheBustId: uuidv4() });
    const resp = await fetch(`${url}?${urlParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            ...authToken ? { Authorization: `Bearer ${authToken}` } : {}
        }
    });

    if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(errorText);
    }

    const json = await resp.json();
    return new Resource(json as repr.User);
};
